import { memo, useState, useCallback, useMemo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import MediaLightbox from '../MediaLightbox';
import GenerationStrip from './GenerationStrip';
import {
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
  IconButton,
  Chip,
  Collapse,
} from '@mui/material';
import { Delete, AutoAwesome, ExpandMore, ExpandLess, VideoFile, PhotoCamera } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { HANDLES, type GenerateNodeData, type GenerationEntry, useCanvasStore } from '../useCanvasStore';
import { studioService } from '../../../../services/studio.service';
import { useStudioStore } from '../../stores/studio.store';
import { getAssetSrc } from '../../utils/assetUrl';
import { useNodeTextField } from './useNodeTextField';
import SaveToLibraryButton from '../../components/SaveToLibraryButton';

const STYLES = [
  'Photorealistic', 'Illustration', '3D Render', 'Flat Design',
  'Watercolor', 'Minimalist', 'Cinematic', 'Pop Art',
];

const ANGLES = [
  { label: 'Eye Level', value: 'eye level' },
  { label: "Bird's Eye", value: "bird's eye view" },
  { label: 'Low Angle', value: 'low angle looking up' },
  { label: 'Dutch Angle', value: 'dutch angle tilted' },
  { label: 'Overhead', value: 'overhead flat lay' },
];

const FOCAL = [
  { label: 'Wide 14mm', value: '14mm wide angle' },
  { label: '50mm', value: '50mm standard lens' },
  { label: '85mm Portrait', value: '85mm portrait lens' },
  { label: '135mm Tele', value: '135mm telephoto lens' },
];

const DOF = [
  { label: 'Shallow (bokeh)', value: 'shallow depth of field with blurred background bokeh' },
  { label: 'Deep (all sharp)', value: 'deep depth of field everything in sharp focus' },
];

function GenerateImageNode({ id, data }: NodeProps & { data: GenerateNodeData }) {
  const updateNodeData = useCanvasStore((s) => s.updateNodeData);
  const removeNode = useCanvasStore((s) => s.removeNode);
  const addAsset = useStudioStore((s) => s.addAsset);
  const projectId = useStudioStore((s) => s.projectId);
  const { enqueueSnackbar } = useSnackbar();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const syncPrompt = useMemo(() => (v: string) => updateNodeData(id, { prompt: v }), [id, updateNodeData]);
  const promptField = useNodeTextField(data.prompt, syncPrompt);

  const handleGenerate = useCallback(async () => {
    if (!data.prompt.trim()) return;
    updateNodeData(id, { isGenerating: true });
    try {
      const options: Record<string, unknown> = { aspectRatio: data.aspectRatio };
      if (data.style) options.style = data.style;
      if (data.cameraAngle || data.focalLength || data.depthOfField) {
        options.camera = {
          ...(data.cameraAngle && { angle: data.cameraAngle }),
          ...(data.focalLength && { focalLength: data.focalLength }),
          ...(data.depthOfField && { depthOfField: data.depthOfField }),
        };
      }
      const res = await studioService.generate(
        data.prompt,
        options,
        projectId || undefined
      );
      if (res.image) {
        const newGen: GenerationEntry = {
          image: res.image,
          mimeType: res.mimeType,
          assetId: res.assetId,
          gcsPath: res.gcsPath,
          prompt: data.prompt,
          timestamp: Date.now(),
        };
        const prevGens = data.generations || [];
        const newGens = [...prevGens, newGen];
        const newIdx = newGens.length - 1;
        updateNodeData(id, {
          resultImage: res.image,
          resultMimeType: res.mimeType,
          assetId: res.assetId,
          gcsPath: res.gcsPath,
          isGenerating: false,
          generations: newGens,
          selectedGeneration: newIdx,
        });
        addAsset({
          type: 'image',
          data: res.image,
          mimeType: res.mimeType,
          prompt: data.prompt,
          tool: 'generate',
          serverId: res.assetId,
        });
      }
    } catch (err) {
      enqueueSnackbar((err as Error).message, { variant: 'error' });
      updateNodeData(id, { isGenerating: false });
    }
  }, [id, data, projectId, updateNodeData, addAsset, enqueueSnackbar]);

  const handleSelectGeneration = useCallback((idx: number) => {
    const gens = data.generations || [];
    const gen = gens[idx];
    if (!gen) return;
    updateNodeData(id, {
      selectedGeneration: idx,
      resultImage: gen.image,
      resultMimeType: gen.mimeType,
      assetId: gen.assetId,
      gcsPath: gen.gcsPath,
      ...(gen.prompt !== undefined && { prompt: gen.prompt }),
    });
  }, [id, data.generations, updateNodeData]);

  const handleExtractFrame = useCallback(async () => {
    // Find connected video node's jobId by traversing edges
    const state = useCanvasStore.getState();
    const edges = state.edges || [];
    const nodes = state.nodes || [];
    const incomingEdge = edges.find(e => e.target === id && e.targetHandle === HANDLES.extractVideoIn);
    let jobId = data.sourceVideoJobId;
    if (!jobId && incomingEdge) {
      const sourceNode = nodes.find(n => n.id === incomingEdge.source);
      jobId = (sourceNode?.data as any)?.jobId;
    }
    if (!jobId) {
      enqueueSnackbar('Connect a completed video node first', { variant: 'warning' });
      return;
    }
    updateNodeData(id, { isGenerating: true });
    try {
      const res = await studioService.extractFrame(
        jobId,
        data.extractTimestamp || 'last',
      );
      const newGen: GenerationEntry = {
        image: res.signedUrl,
        mimeType: 'image/png',
        gcsPath: res.gcsPath,
        prompt: `Extracted ${data.extractTimestamp || 'last'} frame`,
        timestamp: Date.now(),
      };
      const prevGens = data.generations || [];
      const newGens = [...prevGens, newGen];
      updateNodeData(id, {
        resultImage: res.signedUrl,
        resultMimeType: 'image/png',
        gcsPath: res.gcsPath,
        isGenerating: false,
        generations: newGens,
        selectedGeneration: newGens.length - 1,
      });
    } catch (err) {
      enqueueSnackbar((err as Error).message, { variant: 'error' });
      updateNodeData(id, { isGenerating: false });
    }
  }, [id, data, updateNodeData, enqueueSnackbar]);

  const mode = data.mode || 'generate';

  const toggle = (field: string, value: string) => {
    updateNodeData(id, { [field]: data[field] === value ? '' : value });
  };

  const hasAdvanced = !!(data.style || data.cameraAngle || data.focalLength || data.depthOfField);

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        width: 280,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 0.75,
          bgcolor: 'primary.dark',
          color: 'primary.contrastText',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {mode === 'extract' ? <VideoFile sx={{ fontSize: 16 }} /> : <AutoAwesome sx={{ fontSize: 16 }} />}
          <Typography variant="caption" fontWeight={600}>
            {mode === 'extract' ? 'Extract Frame' : 'Generate Image'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={() => removeNode(id)} sx={{ color: 'inherit' }}>
          <Delete sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      {/* Video input handle for extract mode */}
      <Handle
        type="target"
        position={Position.Left}
        id={HANDLES.extractVideoIn}
        style={{ background: '#f48fb1', width: 10, height: 10, top: '50%' }}
      />

      {/* Body */}
      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* Mode toggle */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button
            size="small" variant={mode === 'generate' ? 'contained' : 'outlined'}
            onClick={() => updateNodeData(id, { mode: 'generate' })}
            sx={{ flex: 1, fontSize: '0.7rem', py: 0.3 }}
            startIcon={<AutoAwesome sx={{ fontSize: 12 }} />}
          >
            Generate
          </Button>
          <Button
            size="small" variant={mode === 'extract' ? 'contained' : 'outlined'}
            onClick={() => updateNodeData(id, { mode: 'extract' })}
            sx={{ flex: 1, fontSize: '0.7rem', py: 0.3 }}
            startIcon={<PhotoCamera sx={{ fontSize: 12 }} />}
          >
            Extract
          </Button>
        </Box>

        {mode === 'extract' ? (
          <>
            <Select
              size="small"
              value={data.extractTimestamp || 'last'}
              onChange={(e) => updateNodeData(id, { extractTimestamp: e.target.value })}
              fullWidth
              className="nodrag"
            >
              <MenuItem value="last">Last frame</MenuItem>
              <MenuItem value="first">First frame</MenuItem>
              <MenuItem value="0.5">0.5s</MenuItem>
              <MenuItem value="1">1s</MenuItem>
              <MenuItem value="2">2s</MenuItem>
              <MenuItem value="3">3s</MenuItem>
              <MenuItem value="4">4s</MenuItem>
              <MenuItem value="5">5s</MenuItem>
            </Select>
            <Typography variant="caption" color="text.secondary">
              Connect a video node to the left input
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={handleExtractFrame}
              disabled={data.isGenerating}
              startIcon={data.isGenerating ? <CircularProgress size={14} /> : <PhotoCamera sx={{ fontSize: 14 }} />}
              fullWidth
            >
              {data.isGenerating ? 'Extracting…' : 'Extract Frame'}
            </Button>
          </>
        ) : (
          <>
        <TextField
          size="small"
          multiline
          minRows={2}
          maxRows={4}
          placeholder="Describe the image..."
          value={promptField.value}
          onChange={promptField.onChange}
          onFocus={promptField.onFocus}
          onBlur={promptField.onBlur}
          fullWidth
          className="nodrag nowheel"
        />

        <Select
          size="small"
          value={data.aspectRatio}
          onChange={(e) => updateNodeData(id, { aspectRatio: e.target.value as GenerateNodeData['aspectRatio'] })}
          fullWidth
          className="nodrag"
        >
          <MenuItem value="1:1">1:1 Square</MenuItem>
          <MenuItem value="3:4">3:4 Portrait</MenuItem>
          <MenuItem value="4:3">4:3 Landscape</MenuItem>
          <MenuItem value="9:16">9:16 Tall</MenuItem>
          <MenuItem value="16:9">16:9 Wide</MenuItem>
        </Select>

        {/* Advanced toggle */}
        <Button
          size="small"
          variant="outlined"
          onClick={() => setShowAdvanced(!showAdvanced)}
          endIcon={showAdvanced ? <ExpandLess sx={{ fontSize: 14 }} /> : <ExpandMore sx={{ fontSize: 14 }} />}
          sx={{ textTransform: 'none', fontSize: '0.75rem' }}
          fullWidth
        >
          🎨 Style & Camera{hasAdvanced ? ' ●' : ''}
        </Button>

        <Collapse in={showAdvanced} className="nodrag nowheel">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, pt: 0.5 }}>
            {/* Style */}
            <Typography variant="caption" color="text.secondary">Style</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {STYLES.map((s) => (
                <Chip
                  key={s}
                  label={s}
                  size="small"
                  variant={data.style === s.toLowerCase() ? 'filled' : 'outlined'}
                  color={data.style === s.toLowerCase() ? 'primary' : 'default'}
                  onClick={() => toggle('style', s.toLowerCase())}
                  sx={{ fontSize: '0.65rem', height: 22 }}
                />
              ))}
            </Box>

            {/* Camera Angle */}
            <Typography variant="caption" color="text.secondary">Camera Angle</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {ANGLES.map((a) => (
                <Chip
                  key={a.value}
                  label={a.label}
                  size="small"
                  variant={data.cameraAngle === a.value ? 'filled' : 'outlined'}
                  color={data.cameraAngle === a.value ? 'primary' : 'default'}
                  onClick={() => toggle('cameraAngle', a.value)}
                  sx={{ fontSize: '0.65rem', height: 22 }}
                />
              ))}
            </Box>

            {/* Focal Length */}
            <Typography variant="caption" color="text.secondary">Focal Length</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {FOCAL.map((f) => (
                <Chip
                  key={f.value}
                  label={f.label}
                  size="small"
                  variant={data.focalLength === f.value ? 'filled' : 'outlined'}
                  color={data.focalLength === f.value ? 'primary' : 'default'}
                  onClick={() => toggle('focalLength', f.value)}
                  sx={{ fontSize: '0.65rem', height: 22 }}
                />
              ))}
            </Box>

            {/* Depth of Field */}
            <Typography variant="caption" color="text.secondary">Depth of Field</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {DOF.map((d) => (
                <Chip
                  key={d.value}
                  label={d.label}
                  size="small"
                  variant={data.depthOfField === d.value ? 'filled' : 'outlined'}
                  color={data.depthOfField === d.value ? 'primary' : 'default'}
                  onClick={() => toggle('depthOfField', d.value)}
                  sx={{ fontSize: '0.65rem', height: 22 }}
                />
              ))}
            </Box>
          </Box>
        </Collapse>

        <Button
          variant="contained"
          size="small"
          onClick={handleGenerate}
          disabled={data.isGenerating || !data.prompt.trim()}
          startIcon={data.isGenerating ? <CircularProgress size={14} /> : <AutoAwesome sx={{ fontSize: 14 }} />}
          fullWidth
        >
          {data.isGenerating ? 'Generating…' : 'Generate'}
        </Button>
          </>
        )}

        {/* Result preview */}
        {data.resultImage && (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: data.aspectRatio.replace(':', '/'),
              borderRadius: 1,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <img
              src={getAssetSrc(data.resultImage, data.resultMimeType || 'image/png')}
              alt="Generated"
              style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
              onClick={() => setLightbox(true)}
            />
            <SaveToLibraryButton assetId={data.assetId} defaultName={data.prompt} />
          </Box>
        )}
        {data.resultImage && (
          <MediaLightbox
            open={lightbox}
            onClose={() => setLightbox(false)}
            src={getAssetSrc(data.resultImage, data.resultMimeType || 'image/png')}
            type="image"
          />
        )}

        {/* Generation history strip */}
        {data.generations && data.generations.length > 1 && (
          <GenerationStrip
            generations={data.generations}
            selectedIndex={data.selectedGeneration ?? data.generations.length - 1}
            onSelect={handleSelectGeneration}
            type="image"
          />
        )}
      </Box>

      {/* Output handle */}
      {data.resultImage && (
        <Handle
          type="source"
          position={Position.Right}
          id={HANDLES.imageOut}
          style={{ background: '#90caf9', width: 10, height: 10 }}
        />
      )}
    </Box>
  );
}

export default memo(GenerateImageNode);
